import { useState, useMemo, useCallback } from 'react';
import { useMembershipData } from '../../hooks/useMembershipData';
import { useUserAssignedBrgs } from '../../hooks/useUserAssignedBrgs';
import { useAuth } from '../../auth/useAuth';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ExportButton } from '../../components/shared/ExportButton';
import { FilterSection } from './MembershipData/FilterSection';
import { StatsPanel } from './MembershipData/StatsPanel';
import { ChartSection } from './MembershipData/ChartSection';
import { BrgColorKey } from './MembershipData/BrgColorKey';
import { buildExportFileName, buildScopeLabel, exportToExcel } from '../../lib/export';
import { brgColorOrDefault } from '../../lib/colorUtils';
import {
  buildMembershipExportFilters,
  buildMembershipExportSheets,
} from './MembershipData/membershipExport';

export function MembershipDataPage() {
  const [selectedCompany, setSelectedCompany] = useState<string>('All Companies');
  const [selectedBrg, setSelectedBrg] = useState<number | null>(null);
  const [chartType, setChartType] = useState<'bar' | 'pie'>('pie');

  const { roles, name: userName } = useAuth();

  // Fetch BRG list for filter dropdown (Leads see only assigned BRGs, Admins see all)
  const brgQuery = useUserAssignedBrgs();

  // Fetch membership data
  const membershipQuery = useMembershipData({
    company: selectedCompany === 'All Companies' ? undefined : selectedCompany,
    brgId: selectedBrg ?? undefined,
  });

  const isLoading = brgQuery.isLoading || membershipQuery.isLoading;
  const hasError = brgQuery.isError || membershipQuery.isError;
  const errorMessage = brgQuery.error?.message ?? membershipQuery.error?.message ?? 'Failed to load data';

  // Prepare BRG list for filters
  const brgFilterList = useMemo(() => {
    if (!brgQuery.data) return [];
    return brgQuery.data
      .map((brg) => ({ brgId: brg.brgId, name: brg.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brgQuery.data]);

  // Build color map from fetched BRG data
  const colorMap = useMemo(() => {
    const map = new Map<number, { name: string; color: string }>();
    if (brgQuery.data) {
      brgQuery.data.forEach((brg) => {
        map.set(brg.brgId, {
          name: brg.name,
          color: brgColorOrDefault(brg.colorCode), // Default to gray if no color code
        });
      });
    }
    return map;
  }, [brgQuery.data]);

  // Get company list from membership data
  const companies = useMemo(() => {
    return membershipQuery.data?.companies ?? [];
  }, [membershipQuery.data]);

  // Determine if we should show involvement breakdown
  // Only show when "All BRGs" is selected AND multiple BRGs are available
  const showInvolvementBreakdown = selectedBrg === null && brgFilterList.length > 1;

  // Handle clear filters
  const handleClearFilters = () => {
    setSelectedCompany('All Companies');
    setSelectedBrg(null);
  };

  const handleExportExcel = useCallback(async () => {
    const summary = membershipQuery.data;
    if (!summary) throw new Error('Membership data is not loaded yet.');

    const brgNameById = new Map(brgFilterList.map((brg) => [brg.brgId, brg.name] as const));

    await exportToExcel(
      {
        title: 'BRG Membership Data',
        fileName: buildExportFileName('BRG-Membership-Data'),
        filters: buildMembershipExportFilters({
          selectedCompany,
          selectedBrgId: selectedBrg,
          brgNameById,
        }),
        // The API already scopes membership data to the caller's BRGs.
        scopeLabel: buildScopeLabel(roles, brgFilterList.map((brg) => brg.name)),
        exportedBy: userName,
      },
      buildMembershipExportSheets(summary, showInvolvementBreakdown)
    );
  }, [
    membershipQuery.data,
    brgFilterList,
    selectedCompany,
    selectedBrg,
    showInvolvementBreakdown,
    roles,
    userName,
  ]);

  if (isLoading) {
    return <LoadingSpinner message="Loading membership data..." fullPage />;
  }

  if (hasError) {
    return (
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ margin: '0 0 20px', fontSize: '1.6rem', color: '#1a1a1a' }}>
          Membership Data
        </h1>
        <div
          style={{
            backgroundColor: '#fdf3f3',
            border: '1px solid #f5c2c2',
            borderRadius: '4px',
            padding: '16px',
            color: '#d32f2f',
          }}
        >
          <strong>Error:</strong> {errorMessage}
        </div>
      </div>
    );
  }

  const data = membershipQuery.data;
  if (!data) return null;

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      {/* Filters */}
      <FilterSection
        companies={companies}
        brgs={brgFilterList}
        selectedCompany={selectedCompany}
        selectedBrg={selectedBrg}
        onCompanyChange={setSelectedCompany}
        onBrgChange={setSelectedBrg}
        chartType={chartType}
        onChartTypeChange={setChartType}
        exportAction={
          <ExportButton
            format="excel"
            onExport={handleExportExcel}
            disabled={data.brgMemberships.length === 0}
            disabledReason="There is no membership data to export for the current filters."
            testId="export-membership-excel"
          />
        }
      />

      {/* Main Layout: Stats on Left, Chart on Right */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
        {/* Left Column: Stats Panel - always visible */}
        <div style={{ flex: '0 0 300px', minWidth: '300px' }}>
          {data && (
            <StatsPanel data={data} showInvolvementBreakdown={showInvolvementBreakdown} />
          )}
        </div>

        {/* Right Column: Chart */}
        <div style={{ flex: '1 1 auto', minWidth: '500px' }}>
          {data.brgMemberships.length === 0 ? (
            <div
              style={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '40px 20px',
                textAlign: 'center',
              }}
            >
              <h3 style={{ margin: '0 0 40px', fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a' }}>
                Membership by BRG
              </h3>
              <div style={{ marginBottom: '16px' }}>
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="35" cy="35" r="24" stroke="#b0b8c9" strokeWidth="3" fill="none" />
                  <line x1="53" y1="53" x2="68" y2="68" stroke="#b0b8c9" strokeWidth="4" strokeLinecap="round" />
                  <line x1="25" y1="35" x2="45" y2="35" stroke="#b0b8c9" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a' }}>
                No members match the selected filters.
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#666' }}>
                Try adjusting your filters to see results.
              </p>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#fff',
                  color: '#1a237e',
                  border: '2px solid #1a237e',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <ChartSection
              data={data.brgMemberships}
              colorMap={colorMap}
              isLoading={membershipQuery.isLoading}
              chartType={chartType}
            />
          )}
        </div>
      </div>

      {/* BRG Color Key */}
      <BrgColorKey colorMap={colorMap} />
    </div>
  );
}
